import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import { isSameDayUTC } from 'lib/datetime/dateUtils'
import productLabels from 'i18n/productLabels'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE, LOYALTY_WELCOME_POINT_EXPIRY_DATE_1, LOYALTY_WELCOME_POINT_EXPIRY_DATE_2, LOYALTY_WELCOME_POINT_EXPIRY_DATE_3 } from 'loyalty/constants/loyaltyConfig'

type ServerLoyaltyActivity = paths['/api/lux-loyalty/account/activities']['get']['responses']['200']['content']['application/json']['result']['activities'][0]

function getLocalActivityType(activity: ServerLoyaltyActivity): App.LoyaltyAccountActivityType {
  switch (activity.activityType) {
    // case 'Accrual Activity':

    case 'Cancellation Activity':
      return 'cancellation'
    case 'Expire Points':
      return 'expired_points'
    case 'Expired Points Reversal':
      return 'expired_points_reversal'
    case 'Manual Adjustment':
      return 'manual'
    case 'Member Enrolment Activity':
      return 'enrolment'
    case 'Accrual Activity':
      if (activity.basePoints) {
        if (activity.basePoints > 0) {
          return 'earn_points'
        } else if (activity.basePoints < 0) {
          if (!activity.orderId) {
            // special case for the welcome points expiry adjustment
            // Only need to check this for base points, as the people only have 660 as welcome points won't have any bonus points
            if (isExpireWelcomePointsActivity(activity.basePoints, activity.activityDate)) {
              return 'expired_points'
            }
            // if there's no orderId and it's on the point deduction cut off date, it's the points ratio adjust
            if (isSameDayUTC(new Date(activity.activityDate), new Date(LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE)))
            { return 'points_ratio_adjust' }
            return 'debit_points'
          }
          return 'debit_points'
        }
      } else if (activity.bonusPoints) {
        return activity.bonusPoints > 0 ? 'earn_points' : 'debit_points'
      } else if (activity.statusCredits || activity.bonusStatusCredits) {
        return 'earn_status_credits'
      } else if (activity.numNights) {
        return activity.numNights > 0 ? 'credit_nights' : 'debit_nights'
      }
      // not sure what else could accrue
      return 'other'
    case 'Adhoc Activity':
      return 'ad_hoc'
    case 'Redemption':
      if (activity.basePoints) {
        return activity.basePoints > 0 ? 'earn_points' : 'spend_points'
      } else if (activity.bonusPoints) {
        return activity.bonusPoints > 0 ? 'earn_points' : 'spend_points'
      } else if (activity.statusCredits) {
        return 'earn_status_credits'
      } if (activity.itemId) {
        return 'benefit_redemption'
      }
      return 'other'
    case 'Tier Change Activity':
      return 'tier_change'
    default:
      return 'other'
  }
}

/**
 * Checks if the given activity is an expiration of welcome points.
 * @param activity The loyalty activity to check.
 * @returns True if the activity is an expiration of welcome points, false otherwise.
 */
export function isExpireWelcomePointsActivity(basePoints: number, activityDate: string): boolean {
  return basePoints === -660
    && (isSameDayUTC(new Date(activityDate), new Date(LOYALTY_WELCOME_POINT_EXPIRY_DATE_1))
      || isSameDayUTC(new Date(activityDate), new Date(LOYALTY_WELCOME_POINT_EXPIRY_DATE_2))
      || isSameDayUTC(new Date(activityDate), new Date(LOYALTY_WELCOME_POINT_EXPIRY_DATE_3)))
}

/**
 * Checks if the given activity is a Lux Plus renewal cancellation.
 * @param activity The loyalty activity to check.
 * @returns True if the activity is a Lux Plus renewal cancellation, false otherwise.
 */
function isLuxPlusRenewalCancellationActivity(activity: ServerLoyaltyActivity): boolean {
  return (activity.activityType === 'Accrual Activity' || activity.activityType === 'Manual Adjustment')
    && activity.bonusPoints === -7500
    && activity.statusCredits === -100
    && !activity.orderId
}

/**
 * This is a temporary measure to sanitise the incoming description for particular activities.
 */
function mapLoyaltyAccountActivityDescription(activity: ServerLoyaltyActivity): string | undefined {
  if (isLuxPlusRenewalCancellationActivity(activity)) {
    const intl = getCurrentIntl()

    return intl.formatMessage(
      {
        id: 'uO7m0rvI7JDcFiFZypj2',
        defaultMessage: '{programName} renewal cancellation',
        description: 'Fallback loyalty activity description shown when a LuxPlus+ renewal cancellation activity needs a localized label',
      },
      { programName: intl.formatMessage(productLabels.luxPlus) },
    )
  }
  return activity.activityDescription
}

function mapLoyaltyAccountActivity(activity: ServerLoyaltyActivity): App.LoyaltyAccountActivity {
  const basePoints = activity.basePoints ?? 0
  const bonusPoints = activity.bonusPoints ?? 0

  return {
    id: activity.id,
    date: activity.activityDate,
    type: getLocalActivityType(activity),
    description: mapLoyaltyAccountActivityDescription(activity),
    points: basePoints + bonusPoints,
    bonusPoints,
    basePoints,
    orderId: activity.orderId,
    orderItemId: activity.itemId,
    statusCredits: (activity.statusCredits ?? 0) + (activity.bonusStatusCredits ?? 0),
    numNights: activity.numNights,
    benefitCode: activity.benefitCode,
    tierFrom: activity.tierFrom,
    tierTo: activity.tierTo,
    tierExpiry: activity.tierExpiry,
  }
}

export default mapLoyaltyAccountActivity
